import type React from 'react'
import {useCallback, useState} from 'react'
import {Link, Heading, Image} from '@primer/react-brand'
import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'

import AutoPlayButton, {TRANSITION_DURATION, type AutoPlayButtonProps} from '../AutoPlayButton/AutoPlayButton'
import {wait} from '../../utils/time'

export interface SectionHeroProps {
  visuals: Array<{
    url: string
    alt: string
  }>
  autoPlayButtonAriaLabel: AutoPlayButtonProps['ariaLabel']
  text?: {
    title: string
    description: string
    link: {
      url: string
      label: string
    }
  }
  analyticsId?: string
}

const SectionHero: React.FC<SectionHeroProps> = ({visuals, text, autoPlayButtonAriaLabel, analyticsId}) => {
  const [currentVisualIndex, setCurrentVisualIndex] = useState<number>(0)
  const [isVisualVisible, setIsVisualVisible] = useState<boolean>(true)

  const onVisualTimeout = useCallback(async () => {
    setIsVisualVisible(false)
    await wait((TRANSITION_DURATION / 2) * 1000)

    setCurrentVisualIndex(prevIndex => (prevIndex + 1) % visuals.length)
    await wait((TRANSITION_DURATION / 2) * 1000)

    setIsVisualVisible(true)
  }, [visuals?.length])

  return (
    <div className="lp-SectionHero">
      {/* TODO: Adjust background */}
      <div className="lp-SectionHero-background" />

      {visuals.length > 1 && (
        <div className="lp-SectionHero-autoPlayButton p-responsive">
          <AutoPlayButton onTimeout={onVisualTimeout} ariaLabel={autoPlayButtonAriaLabel} />
        </div>
      )}

      <div className={`lp-SectionHero-content ${text ? 'lp-SectionHero-content--hasText' : ''}`}>
        {text ? (
          <div className="lp-SectionHero-text">
            <Heading as="h3" size="6" weight="semibold" className="lp-SectionHero-text-title">
              {text.title}
              <span>{text.description}</span>
            </Heading>

            <div>
              <Link
                href={text.link.url}
                variant="accent"
                className="lp-SectionHero-text-link"
                {...getAnalyticsEvent({
                  action: text.link.label,
                  tag: 'link',
                  context: 'section_hero',
                  location: analyticsId,
                })}
              >
                {text.link.label}
              </Link>
            </div>
          </div>
        ) : null}

        <div className="lp-SectionHero-visual">
          <div
            className={`lp-SectionHero-visual-frame ${!isVisualVisible ? 'lp-SectionHero-visual-frame--hidden' : ''}`}
          >
            {visuals[currentVisualIndex] && (
              <Image src={visuals[currentVisualIndex].url} alt={visuals[currentVisualIndex].alt} />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default SectionHero

try{ SectionHero.displayName ||= 'SectionHero' } catch {}